<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import { useBasePriceStore } from "@/modules/base_price/store";
import ResolvedNodeDTO = Modules.BasePrice.Data.Resolved.ResolvedNodeDTO;
import ResolvedAssetDTO = Modules.BasePrice.Data.Resolved.ResolvedAssetDTO;

const props = defineProps<{ node: ResolvedNodeDTO }>();

const store = useBasePriceStore();

const children = computed(() => store.childrenOf(props.node.hierarchy_node_id));

/**
 * The individual screens attached to this exact node (see
 * ResolvedNodeDTO.assets). A screen's % share is always read-only (no
 * SplitOverrideDTO counterpart exists at the screen level) — but its
 * Budget can be fixed, via AssetFixedBudgetOverrideDTO, the screen-level
 * equivalent of a hierarchy node's fixed-budget override. Laravel
 * serializes this Collection as a JSON array when its keys are consecutive
 * but the generated TS type also allows a keyed object for the general
 * case — same normalization every other collection in this store needs
 * (see store.ts).
 */
const assets = computed<ResolvedAssetDTO[]>(() =>
	props.node.assets ? Object.values(props.node.assets) : []
);

/**
 * What this node was handed to divide, not what it resolves to: `budget` is
 * the sum of its rows, so measuring the rows against it would quietly move
 * the target to whatever was typed — over- and under-commitment would both
 * read as "Allocated €X of €X".
 */
const allotted = computed(() => props.node.allotted_budget);

const actualSharePercent = (child: ResolvedNodeDTO): number =>
	allotted.value > 0 ? (child.allotted_budget / allotted.value) * 100 : 0;

const round1 = (value: number): number => Math.round(value * 10) / 10;

/**
 * Two always-visible inputs per row: a % share and a € budget. Editing
 * either one sets the corresponding override and clears the other (see
 * store.setSplitShare()/setFixedBudget()) — a row is never in "% mode" or
 * "€ mode", both fields just always reflect the current effective figure,
 * and whichever one you actually type into decides what gets saved. The %
 * input is disabled (but still shown, computed) once a fixed budget is set,
 * since a fixed-budget row's % is a derived consequence, not something you
 * can edit directly — you'd have to reset it first.
 *
 * Seeded from the resolver's actual figures on node change; kept in sync
 * locally (without waiting for the debounced resolve round-trip) by
 * mirroring BasePriceBookResolver::budgetForChild()'s own-override-or-
 * impression-weighted-share-of-remainder logic against the store's live
 * draft overrides — see computeEffectiveBudget().
 */
const draftPercent = reactive<Record<number, number>>({});
const draftBudget = reactive<Record<number, number>>({});

/**
 * A screen's Budget, keyed by asset_id (a separate id space from
 * hierarchy_node_id, hence its own record rather than sharing draftBudget).
 * Its % (draftAssetPercent, below) is always disabled but still refreshed
 * live on every edit — only Budget is ever editable for a screen.
 */
const draftAssetBudget = reactive<Record<number, number>>({});

/**
 * A screen's displayed %, refreshed instantly on every edit rather than
 * waiting for the debounced resolve — mirrors `asset.share`'s own
 * definition (actual budget ÷ node's total budget, not an impressions
 * ratio), so it updates the moment *any* budget changes, exactly like
 * draftPercent already does for children.
 */
const draftAssetPercent = reactive<Record<number, number>>({});

const resetDrafts = () => {
	Object.keys(draftPercent).forEach((key) => delete draftPercent[Number(key)]);
	Object.keys(draftBudget).forEach((key) => delete draftBudget[Number(key)]);
	Object.keys(draftAssetBudget).forEach((key) => delete draftAssetBudget[Number(key)]);
	Object.keys(draftAssetPercent).forEach((key) => delete draftAssetPercent[Number(key)]);

	children.value.forEach((child) => {
		draftPercent[child.hierarchy_node_id] = round1(actualSharePercent(child));
		draftBudget[child.hierarchy_node_id] = Math.round(child.allotted_budget);
	});

	assets.value.forEach((asset) => {
		draftAssetBudget[asset.asset_id] = Math.round(asset.budget);
		draftAssetPercent[asset.asset_id] = round1(asset.share);
	});
};

// Keyed on the book as well as the node: every book runs its own hierarchy
// with its own splits, so switching book changes every figure in this table.
// Without it the inputs kept the previous book's percentages and euro amounts
// — resetDrafts() simply never ran — while the read-only columns beside them
// had already moved to the new book.
watch(
	() => [props.node.hierarchy_node_id, store.selectedBookUuid],
	resetDrafts,
	{ immediate: true }
);

const shareHint = (child: ResolvedNodeDTO): string => {
	if (child.fixed_budget_own) return "fixed budget";
	if (child.split_own) return "budget override";
	return "share of remainder based on impressions";
};

const hasOwnOverride = (child: ResolvedNodeDTO): boolean =>
	child.split_own || child.fixed_budget_own;

/** This node's own split override % in the current draft, if any — mirrors budgetForChild()'s "own" lookup. */
const draftSplitOf = (hierarchyNodeId: number): number | null => {
	const override = store.draftOverrides.find(
		(o) =>
			o.book_uuid === store.selectedBook?.uuid &&
			o.hierarchy_node_id === hierarchyNodeId &&
			o.type === "split"
	);
	return override?.percentage ?? null;
};

/** This node's own fixed-budget override € amount in the current draft, if any. */
const draftFixedBudgetOf = (hierarchyNodeId: number): number | null => {
	const override = store.draftOverrides.find(
		(o) =>
			o.book_uuid === store.selectedBook?.uuid &&
			o.hierarchy_node_id === hierarchyNodeId &&
			o.type === "fixed_budget"
	);
	return override?.amount ?? null;
};

/** This screen's own fixed-budget override € amount in the current draft, if any. */
const draftAssetFixedOf = (assetId: number): number | null => {
	const override = store.draftOverrides.find(
		(o) =>
			o.book_uuid === store.selectedBook?.uuid &&
			o.type === "asset_fixed_budget" &&
			o.asset_id === assetId
	);
	return override?.amount ?? null;
};

/**
 * The combined pinned/free tally across every child AND every screen on
 * this node — mirrors BasePriceBookResolver::resolveBranch()'s unified pool
 * exactly (a fixed/%-overridden child and a fixed screen both pull their
 * euro amount out first, and everything left free — children and screens
 * alike — shares the true remainder together, weighted by impressions).
 * There is no separate "screens bucket" size decided upfront; fixing one
 * screen reshuffles sibling *children* too, and vice versa.
 *
 * Uses each child's/screen's already-resolved `gross_impressions` as the
 * weighting — an approximation of the backend's real weighting (which uses
 * raw, un-reserved impressions specifically to avoid a reservation-tracking
 * side effect, see BasePriceBookResolver::rawGrossImpressionsUnder()). The
 * two can differ slightly for nodes affected by a reservation book's
 * impression share; this local preview self-corrects to the exact backend
 * figure once the debounced resolve lands.
 */
const combinedFreePool = computed(() => {
	let pinnedTotal = 0;
	const freeChildren: ResolvedNodeDTO[] = [];
	const freeAssets: ResolvedAssetDTO[] = [];

	children.value.forEach((c) => {
		const fixed = draftFixedBudgetOf(c.hierarchy_node_id);
		const pct = draftSplitOf(c.hierarchy_node_id);

		if (fixed !== null) {
			pinnedTotal += fixed;
		} else if (pct !== null) {
			pinnedTotal += (allotted.value * pct) / 100;
		} else {
			freeChildren.push(c);
		}
	});

	assets.value.forEach((a) => {
		const fixed = draftAssetFixedOf(a.asset_id);
		if (fixed !== null) {
			pinnedTotal += fixed;
		} else {
			freeAssets.push(a);
		}
	});

	const remaining = Math.max(0, allotted.value - pinnedTotal);
	const totalFreeWeight =
		freeChildren.reduce((sum, c) => sum + c.gross_impressions, 0) +
		freeAssets.reduce((sum, a) => sum + a.gross_impressions, 0);
	const freeCount = freeChildren.length + freeAssets.length;

	return { remaining, totalFreeWeight, freeCount };
});

const shareOfRemainingPool = (weight: number): number => {
	const { remaining, totalFreeWeight, freeCount } = combinedFreePool.value;

	if (totalFreeWeight > 0) return remaining * (weight / totalFreeWeight);

	return freeCount > 0 ? remaining / freeCount : 0;
};

const computeEffectiveBudget = (child: ResolvedNodeDTO): number => {
	const fixed = draftFixedBudgetOf(child.hierarchy_node_id);
	if (fixed !== null) return fixed;

	const pct = draftSplitOf(child.hierarchy_node_id);
	if (pct !== null) return (allotted.value * pct) / 100;

	return shareOfRemainingPool(child.gross_impressions);
};

/** Mirrors the backend's per-screen split inside resolveBranch()/resolveLeafAssets() — same combined pool as computeEffectiveBudget(), just for a screen's own weight. */
const computeEffectiveAssetBudget = (asset: ResolvedAssetDTO): number => {
	const fixed = draftAssetFixedOf(asset.asset_id);
	if (fixed !== null) return fixed;

	return shareOfRemainingPool(asset.gross_impressions);
};

const refreshDrafts = () => {
	children.value.forEach((child) => {
		const budget = computeEffectiveBudget(child);
		draftBudget[child.hierarchy_node_id] = Math.round(budget);
		draftPercent[child.hierarchy_node_id] = round1(
			allotted.value > 0 ? (budget / allotted.value) * 100 : 0
		);
	});

	assets.value.forEach((asset) => {
		const budget = computeEffectiveAssetBudget(asset);
		draftAssetBudget[asset.asset_id] = Math.round(budget);
		draftAssetPercent[asset.asset_id] = round1(
			allotted.value > 0 ? (budget / allotted.value) * 100 : 0
		);
	});
};

/**
 * Reaches the resolver: store.setSplitShare() sets a % override and clears
 * any fixed-budget one on the same node, and the budget/impressions/sellable/
 * CPM columns follow.
 *
 * Bound to `change` rather than `input`, so it fires once the figure is
 * settled — on blur, on Enter, or on a spinner click — instead of on every
 * keystroke. Typing "25" into an empty box used to resolve the whole plan
 * against "2" first, which is both wasted work and a visible flicker through
 * a split nobody asked for.
 *
 * Disabled in the template once the row has a fixed budget, so this only
 * ever fires from a free or %-overridden row.
 */
const onPercentChange = (child: ResolvedNodeDTO, value: number) => {
	if (!store.selectedBook) return;

	store.setSplitShare(store.selectedBook.uuid, child.hierarchy_node_id, value);
	refreshDrafts();
	// Keep exactly what the user typed on this row — refreshDrafts() would
	// otherwise round it like every other row.
	draftPercent[child.hierarchy_node_id] = value;
};

/**
 * Reaches the resolver: store.setFixedBudget() sets a fixed-€ override and
 * clears any % override on the same node — editing the budget figure is
 * what turns a free or %-overridden row into a fixed-budget one.
 */
const onBudgetChange = (child: ResolvedNodeDTO, value: number) => {
	if (!store.selectedBook) return;

	store.setFixedBudget(store.selectedBook.uuid, child.hierarchy_node_id, value);
	refreshDrafts();
	draftBudget[child.hierarchy_node_id] = value;
};

/**
 * Which row's Budget input is currently focused, if any — while focused it
 * shows the plain digit value (easiest to edit); everywhere else it's
 * rendered with thousands separators via formatNumber() for readability
 * (1000000 → "1,000,000"/"1.000.000" depending on locale). A native
 * type="number" input can't display a grouped string at all, so this one is
 * type="text" with inputmode="numeric" instead.
 */
const focusedBudgetNodeId = ref<number | null>(null);

const displayBudget = (child: ResolvedNodeDTO): string => {
	const raw = draftBudget[child.hierarchy_node_id] ?? 0;
	return focusedBudgetNodeId.value === child.hierarchy_node_id
		? String(raw)
		: formatNumber(raw, "number_no_fraction");
};

const onBudgetInput = (child: ResolvedNodeDTO, rawText: string) => {
	onBudgetChange(child, Number(rawText.replace(/[^0-9]/g, "") || 0));
};

/** Reaches the resolver: store.setAssetFixedBudget() sets this screen's own fixed-€ override. */
const onAssetBudgetChange = (asset: ResolvedAssetDTO, value: number) => {
	if (!store.selectedBook) return;

	store.setAssetFixedBudget(store.selectedBook.uuid, asset.asset_id, value);
	refreshDrafts();
	draftAssetBudget[asset.asset_id] = value;
};

const focusedAssetBudgetId = ref<number | null>(null);

const displayAssetBudget = (asset: ResolvedAssetDTO): string => {
	const raw = draftAssetBudget[asset.asset_id] ?? 0;
	return focusedAssetBudgetId.value === asset.asset_id
		? String(raw)
		: formatNumber(raw, "number_no_fraction");
};

const onAssetBudgetInput = (asset: ResolvedAssetDTO, rawText: string) => {
	onAssetBudgetChange(asset, Number(rawText.replace(/[^0-9]/g, "") || 0));
};

/** Clears this screen's own fixed-budget override, reverting it to an impression-weighted share of the bucket's remainder — a no-op if it has none. */
const onAssetReset = (asset: ResolvedAssetDTO) => {
	if (!store.selectedBook || !asset.fixed_budget_own) return;

	store.setAssetFixedBudget(store.selectedBook.uuid, asset.asset_id, null);
	refreshDrafts();
};

/** Clears this node's own override (whichever kind it is), reverting it to an impression-weighted share of remainder — a no-op if it has neither. */
const onReset = (child: ResolvedNodeDTO) => {
	if (!store.selectedBook || !hasOwnOverride(child)) return;

	store.setDraftOverride(
		store.selectedBook.uuid,
		child.hierarchy_node_id,
		"split",
		null
	);
	store.setDraftOverride(
		store.selectedBook.uuid,
		child.hierarchy_node_id,
		"fixed_budget",
		null
	);
	refreshDrafts();
};

/**
 * Sum of every child's and every screen's *displayed* budget (draftBudget/
 * draftAssetBudget — whatever's literally shown in that row's Budget input
 * right now) — compared against the parent's own budget rather than a %,
 * since a mix of fixed-€ and % rows no longer sums to a meaningful
 * percentage.
 *
 * Deliberately sums the draft records rather than recomputing each row's
 * share independently via computeEffectiveBudget()/computeEffectiveAssetBudget(): those mirrors are only an
 * approximation of the backend's real split (it weights by each child's
 * already-resolved gross_impressions, not the backend's raw, un-reserved
 * figure — see computeEffectiveBudget()'s own doc comment) and can be off
 * by more than the 1-euro tolerance below even at rest, with nothing being
 * edited — which showed as a bright red "Allocated €10.1M of €10.1M" even
 * though every row's own displayed number added up fine. Summing exactly
 * what's rendered guarantees the total can never disagree with the rows
 * sitting right above it.
 */
const allocatedTotal = computed(
	() =>
		children.value.reduce(
			(sum, child) => sum + (draftBudget[child.hierarchy_node_id] ?? 0),
			0
		) +
		assets.value.reduce(
			(sum, asset) => sum + (draftAssetBudget[asset.asset_id] ?? 0),
			0
		)
);

/**
 * Compares the *displayed* strings, not the raw numbers with some fixed
 * tolerance: formatNumberForScale("money", ...) rounds to one decimal at
 * the millions scale, so anything within roughly ±50K still reads as the
 * same "€2.8M" — a flat few-euro tolerance on the raw values doesn't
 * account for that and can flag red even while both sides show identical
 * text (e.g. a small, expected gap between this local preview's own
 * impression-weighted approximation and the real backend split — see
 * allocatedTotal's own doc comment). Tying the color to what's actually
 * rendered means it can never disagree with what the user is looking at.
 */
const allocatedColor = computed(() =>
	formatNumberForScale("money", allocatedTotal.value) ===
	formatNumberForScale("money", allotted.value)
		? "success"
		: "error"
);

/**
 * Spelled out rather than left to the red total: a fixed amount is kept
 * exactly as typed, never trimmed to what fits, so this is the only place
 * the row that doesn't add up gets explained. Same displayed-string test as
 * allocatedColor, so the two can never disagree.
 */
const allocationWarning = computed(() => {
	if (allocatedColor.value === "success") return null;

	const gap = allocatedTotal.value - allotted.value;
	const budget = formatNumberForScale("money", allotted.value);

	return gap > 0
		? `Over budget by ${formatNumberForScale("money", gap)} — the fixed and % shares below ask for more than the ${budget} this level has. Lower one, or reset it to share the remainder.`
		: `${formatNumberForScale("money", -gap)} of ${budget} isn't assigned — every row below is fixed or %, so nothing is left free to take up the rest.`;
});
</script>

<template>
	<div v-if="children.length || assets.length">
		<div class="d-flex align-baseline justify-space-between flex-wrap ga-4 mb-3">
			<div class="text-body-2 text-medium-emphasis" style="max-width: 640px">
				<template v-if="children.length">
					Give each child a share of
					{{ formatNumberForScale("money", allotted) }} in
					{{ store.selectedBook?.name }} — either a % of it or a fixed euro
					budget. Untouched children divide the remainder based on their
					impressions.
					<template v-if="assets.length">
						This node also has screens attached directly (not ideal, but
						valid) — shown below for reference, read-only.
					</template>
				</template>
				<template v-else>
					This is the deepest level in the hierarchy — its budget doesn't
					split any further. These are the individual screens behind it,
					for reference only; their split isn't editable here.
				</template>
			</div>
			<div
				class="text-body-2 font-weight-bold"
				:class="`text-${allocatedColor}`"
			>
				Allocated {{ formatNumberForScale("money", allocatedTotal) }} of
				{{ formatNumberForScale("money", allotted) }}
			</div>
		</div>

		<v-alert v-if="allocationWarning" type="warning" class="mb-3">
			{{ allocationWarning }}
		</v-alert>

		<v-table density="comfortable">
			<thead>
				<tr>
					<th style="width: 30%">Level</th>
					<th style="white-space: nowrap">
						<div class="d-flex align-center justify-end ga-1">
							<span>Budget share</span>
							<div class="reset-slot-spacer"></div>
						</div>
					</th>
					<th>
						<div class="d-flex align-center justify-end ga-1">
							<span>Budget</span>
							<div class="reset-slot-spacer"></div>
						</div>
					</th>
					<th class="text-right">Impressions</th>
					<th class="text-right">Sellable</th>
					<th class="text-right">CPM</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="child in children" :key="child.hierarchy_node_id">
					<td>
						<div>{{ child.name }}</div>
						<div
							class="text-caption"
							:class="
								hasOwnOverride(child) ? 'text-warning' : 'text-medium-emphasis'
							"
						>
							{{ shareHint(child) }}
						</div>
					</td>
					<td style="width: 160px">
						<div class="d-flex align-center justify-end ga-1">
							<div
								class="share-input-box"
								:class="{
									'share-input-box--override': child.split_own,
									'share-input-box--disabled': child.fixed_budget_own,
								}"
							>
								<input
									type="number"
									step="0.1"
									:disabled="child.fixed_budget_own"
									:value="draftPercent[child.hierarchy_node_id]"
									@change="
										onPercentChange(
											child,
											Number(($event.target as HTMLInputElement).value)
										)
									"
								/>
								<span class="share-input-suffix">%</span>
							</div>
							<div class="reset-slot">
								<v-btn
									v-if="child.split_own"
									icon="fa-light fa-arrow-rotate-left"
									size="20"
									variant="text"
									density="comfortable"
									title="Reset to impression-weighted share of remainder"
									@click="onReset(child)"
								/>
							</div>
						</div>
					</td>
					<td style="width: 180px">
						<div class="d-flex align-center justify-end ga-1">
							<div
								class="share-input-box share-input-box--budget"
								:class="{
									'share-input-box--override': child.fixed_budget_own,
									'share-input-box--disabled': child.split_own,
								}"
							>
								<span class="share-input-suffix">€</span>
								<input
									type="text"
									inputmode="numeric"
									:disabled="child.split_own"
									:value="displayBudget(child)"
									@focus="focusedBudgetNodeId = child.hierarchy_node_id"
									@blur="focusedBudgetNodeId = null"
									@change="
										onBudgetInput(
											child,
											($event.target as HTMLInputElement).value
										)
									"
								/>
							</div>
							<div class="reset-slot">
								<v-btn
									v-if="child.fixed_budget_own"
									icon="fa-light fa-arrow-rotate-left"
									size="20"
									variant="text"
									density="comfortable"
									title="Reset to impression-weighted share of remainder"
									@click="onReset(child)"
								/>
							</div>
						</div>
					</td>
					<td class="text-right">
						{{ formatNumberForScale("number_no_fraction", child.gross_impressions) }}
					</td>
					<td class="text-right">
						{{
							formatNumberForScale(
								"number_no_fraction",
								child.sellable_impressions
							)
						}}
					</td>
					<td class="text-right font-weight-bold">
						{{ formatNumber(child.cpm, "money") }}
					</td>
				</tr>
				<tr v-for="asset in assets" :key="asset.asset_id" class="split-asset-row">
					<td>
						<div>{{ asset.name }}</div>
						<div
							class="text-caption"
							:class="asset.fixed_budget_own ? 'text-warning' : 'text-medium-emphasis'"
						>
							{{ asset.fixed_budget_own ? "fixed budget" : "screen" }}
						</div>
					</td>
					<td style="width: 160px">
						<div class="d-flex align-center justify-end ga-1">
							<div class="share-input-box share-input-box--disabled">
								<input
									type="number"
									:value="draftAssetPercent[asset.asset_id]"
									disabled
								/>
								<span class="share-input-suffix">%</span>
							</div>
							<div class="reset-slot"></div>
						</div>
					</td>
					<td style="width: 180px">
						<div class="d-flex align-center justify-end ga-1">
							<div
								class="share-input-box share-input-box--budget"
								:class="{ 'share-input-box--override': asset.fixed_budget_own }"
							>
								<span class="share-input-suffix">€</span>
								<input
									type="text"
									inputmode="numeric"
									:value="displayAssetBudget(asset)"
									@focus="focusedAssetBudgetId = asset.asset_id"
									@blur="focusedAssetBudgetId = null"
									@change="
										onAssetBudgetInput(
											asset,
											($event.target as HTMLInputElement).value
										)
									"
								/>
							</div>
							<div class="reset-slot">
								<v-btn
									v-if="asset.fixed_budget_own"
									icon="fa-light fa-arrow-rotate-left"
									size="20"
									variant="text"
									density="comfortable"
									title="Reset to impression-weighted share of the bucket's remainder"
									@click="onAssetReset(asset)"
								/>
							</div>
						</div>
					</td>
					<td class="text-right">
						{{ formatNumberForScale("number_no_fraction", asset.gross_impressions) }}
					</td>
					<td class="text-right">
						{{
							formatNumberForScale(
								"number_no_fraction",
								asset.sellable_impressions
							)
						}}
					</td>
					<td class="text-right font-weight-bold">
						{{ formatNumber(asset.cpm, "money") }}
					</td>
				</tr>
			</tbody>
			<tfoot>
				<tr class="font-weight-bold">
					<td>{{ node.name }} total</td>
					<td></td>
					<td>
						<div
							class="d-flex align-center justify-end ga-1"
							:class="`text-${allocatedColor}`"
						>
							<span>{{ formatNumberForScale("money", allocatedTotal) }}</span>
							<div class="reset-slot-spacer"></div>
						</div>
					</td>
					<td class="text-right">
						{{ formatNumberForScale("number_no_fraction", node.gross_impressions) }}
					</td>
					<td class="text-right">
						{{
							formatNumberForScale(
								"number_no_fraction",
								node.sellable_impressions
							)
						}}
					</td>
					<td class="text-right">{{ formatNumber(node.cpm, "money") }}</td>
				</tr>
			</tfoot>
		</v-table>
	</div>

	<div v-else class="text-body-2 text-medium-emphasis">
		Nothing to split here — this group has no child groups and no screens
		attached. Its budget follows the share set on its parent.
	</div>
</template>

<style scoped lang="scss">
// A plain bordered box with a native input + "%"/"€" span, rather than a
// Vuetify v-text-field — matches the original design's own structure and
// is far easier to get pixel-tight (v-field's internal label/suffix layout
// fights a compact custom height and left the suffix visually detached from
// the number).
.share-input-box {
	display: inline-flex;
	align-items: center;
	width: 76px;
	height: 30px;
	flex: 0 0 auto;
	border: 1px solid rgba(var(--v-theme-on-surface), 0.25);
	border-radius: 6px;
	padding: 0 8px;
	background: rgb(var(--v-theme-surface));
}

.share-input-box--budget {
	width: 108px;
}

.share-input-box--override {
	border-color: rgb(var(--v-theme-warning));
}

.share-input-box--disabled {
	opacity: 0.55;
}

.share-input-box input {
	flex: 1 1 auto;
	min-width: 0;
	width: 100%;
	border: 0;
	outline: none;
	background: transparent;
	color: inherit;
	font: inherit;
	font-size: 12px;
	font-variant-numeric: tabular-nums;
	text-align: right;
}

.share-input-box input:disabled {
	cursor: not-allowed;
}

.share-input-box input::-webkit-outer-spin-button,
.share-input-box input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.share-input-box input[type="number"] {
	-moz-appearance: textfield;
}

.share-input-suffix {
	margin: 0 3px;
	font-size: 11px;
	color: rgba(var(--v-theme-on-surface), 0.6);
	flex: 0 0 auto;
}

// A fixed-size slot for the reset button — v-btn's own rendered box doesn't
// exactly match a plain placeholder div's width, which shifted the whole
// row a couple of px depending on whether the button was present. Giving
// both states the same slot, and centering the button inside it, keeps the
// input column's right edge perfectly aligned across every row.
// Same width as .reset-slot, invisible — used in the header/footer so their
// text lines up with the input box's own right edge above/below, not the
// cell's outer edge (which also has to leave room for a reset button that
// isn't always there).
.reset-slot-spacer {
	width: 28px;
	flex: 0 0 auto;
}

.reset-slot {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	flex: 0 0 auto;
}

// Read-only screen rows mixed in among the editable child rows above — a
// faint background tint is enough to set them apart, since their disabled
// input boxes already carry the same "can't edit this" signal every other
// disabled row in this table uses.
.split-asset-row {
	background: rgba(var(--v-theme-on-surface), 0.02);
}
</style>
